import { SegmentedControl, Text } from "@marekl/adminer/components";
import { companyName, type Company, type CompanyAccess, type CompanyRole } from "../../lib/queries/companies";
import styles from "./CompanyAccessFields.module.css";

export const COMPANY_ROLE_LABEL: Record<CompanyRole, string> = {
  viewer: "Čtenář",
  editor: "Fakturant",
  manager: "Správce firmy",
};

const ROLES = Object.keys(COMPANY_ROLE_LABEL) as CompanyRole[];

/** "Ukázková s.r.o. (Fakturant), Druhá firma s.r.o. (Čtenář)". */
export function companyAccessSummary(companies: Company[], access: CompanyAccess | undefined): string {
  return companies
    .filter((c) => access?.[c.id])
    .map((c) => `${companyName(c)} (${COMPANY_ROLE_LABEL[access![c.id]]})`)
    .join(", ");
}

const ROLE_HINT: Record<CompanyRole, string> = {
  viewer: "vidí doklady a adresář",
  editor: "vystavuje doklady, spravuje adresář a produkty",
  manager: "navíc mění údaje firmy a číselné řady",
};

/**
 * A user's access to each company of the instance: one row per company with
 * the role as a switch, "—" for none (forms design: Uživatel). An
 * administrator sees every company, so there is nothing to pick.
 */
export function CompanyAccessFields({
  companies,
  value,
  onChange,
  admin,
}: {
  companies: Company[];
  value: CompanyAccess;
  onChange: (next: CompanyAccess) => void;
  admin: boolean;
}) {
  return (
    <section className={styles.access}>
      <div className={styles.accessHead}>
        <h3 className={styles.heading}>Přístup k firmám</h3>
        {!admin && <Text variant="muted">Bez role firmu nevidí.</Text>}
      </div>
      {admin ? (
        <Text variant="muted">Administrátor vidí všechny firmy.</Text>
      ) : (
        companies.map((c) => {
          const role = value[c.id];
          return (
            <div key={c.id} className={styles.row}>
              <div className={styles.company}>
                <span className={styles.name}>{companyName(c)}</span>
                <Text variant="muted">{role ? ROLE_HINT[role] : "bez přístupu"}</Text>
              </div>
              <SegmentedControl
                size="sm"
                aria-label={`Role ve firmě ${companyName(c)}`}
                options={[{ value: "", label: "—" }, ...ROLES.map((r) => ({ value: r, label: COMPANY_ROLE_LABEL[r] }))]}
                value={role ?? ""}
                onChange={(v) => {
                  const next = { ...value };
                  if (v) next[c.id] = v as CompanyRole;
                  else delete next[c.id];
                  onChange(next);
                }}
              />
            </div>
          );
        })
      )}
    </section>
  );
}
